
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';

import { Subject, BehaviorSubject, Observable } from 'rxjs';


import { AppConfig } from '../../../../app.config';
import { HttpClient } from '@angular/common/http';
import { BlahService } from '@headpower/blah-ng';
import { PermitData } from 'src/app/shared/external-request/external-request.model';

/**
 * Task specific property
 */
export class TaskSpecificProperty {
    Id: string;
    TaskId: string;
    TemplateId: string;
    Key: string;
    Title: string;
    SystemProperty: boolean;
    HideFromUI: boolean;
    LockOnUI: boolean;  // NOTE: even though locked on UI, property can still be editable.
    Order: number;
    ColumnStartPos?: number;
    ColumnEndPos?: number;
    Type: string;
    MaxLength?: number;
    Value: string;
    Editable: boolean;
    Mandatory: boolean;
    OptionGroupId: string;
}

/**
 * Option is a property value that can be selected with a dropdown
 */
export class Option {
    Id: string;
    Name: string;
    Order: number;
    OptionGroupId: string;
}

@Injectable()
export class TaskTechnicalDetailsService {

    private _permitRequest: PermitData = null;
    public permitRequest$: BehaviorSubject<PermitData> = new BehaviorSubject(null);

    public properties$: Subject<Array<TaskSpecificProperty>> = new BehaviorSubject(null);
    public property$: Subject<void> = new BehaviorSubject(null);
    public nestedDropdownLockedStates$: Subject<Array<string>> = new BehaviorSubject([]);

    private mandatoryNestedDropdowns = new Array<any>;

    constructor(private http: HttpClient,
        private oauthService: OAuthService,
        private config: AppConfig,
        private blahService: BlahService) { }

    /**
     * Get task specific properties from API and update properties$ stream
     * @param taskId - TaskId
     */
    getProperties(taskId: string): void {
        const url = `${this.config.backendIwmUri}Api/TaskSpecificProperties/?%24filter=TaskId%20eq%20${taskId}`;

        this.http.get(url)
            .subscribe((result: any) => {
                const specificProperties = this.removeEmptyRows(result.value);
                this.properties$.next(specificProperties);
                this.handlePermitRequest(result.value);
                if (Array.isArray(result.value)) {
                    this.mandatoryNestedDropdowns = result.value.filter(t => t.Mandatory === true && t.Type === 'NestedDropdown').map(t => ({
                        Id: t.Id,
                        SelectionMade: false,
                        MandatoryAfterStatus: t.MandatoryAfterStatus
                    }));
                    this.nestedDropdownSelectionChanged();
                }
            });
    }

    /**
     * Get task specific property from API
     * @param propertyId - Property id
     * @returns {Observable<TaskSpecificProperty>} Properties
     */
    getProperty(propertyId: string): Observable<TaskSpecificProperty> {
        const url = `${this.config.backendIwmUri}Api/TaskSpecificProperties(${propertyId})`;

        return this.http.get<TaskSpecificProperty>(url);
    }

    /**
     * Get options for a selectable property
     * @param optionGroupId - Id of the option group
     * @returns {Observable<Array<Option>>} Options
     */
    getOptions(optionGroupId: string): Observable<Array<Option>> {
        const url = `${this.config.backendIwmUri}` +
            `CustomerAdminApi/Options/?%24filter=OptionGroupId%20eq%20${optionGroupId}`;

        return this.http.get(url).pipe(
            map((result: any) => {
                return result.value;
            }))
    }

    /**
     * Update task specific properties
     * @param taskId - TaskId
     * @param properties - List of properties
     */
    updateProperty(property: TaskSpecificProperty): Observable<any> {
        return this.http.post(
            this.config.backendIwmUri + `Api/TaskSpecificProperties(${property.Id})/Default.ChangePropertyValue`,
            { 'Value': property.Value });
    }

    setNestedDropdownSelectionMade(id: string, value: boolean) {
        var tmp = this.mandatoryNestedDropdowns.find(t => t.Id.toLocaleLowerCase() === id.toLocaleLowerCase());
        if (tmp) {
            tmp.SelectionMade = value;
            this.nestedDropdownSelectionChanged();
        }
    }

    private nestedDropdownSelectionChanged() {
        var lockedStates = this.mandatoryNestedDropdowns.filter(t => t.SelectionMade === false).map(t => t.MandatoryAfterStatus)
        this.nestedDropdownLockedStates$.next(lockedStates);
    }

    private handlePermitRequest(properties: TaskSpecificProperty[]) {
        const isPermitRequestTaskType = properties.find(o => o.Key.endsWith('PermitRequestTaskType'));
        if (isPermitRequestTaskType) {

            this._permitRequest = {
                PermitIdentifier: properties.find(o => o.Key.endsWith('_PermitIdentifier'))?.Value || '',
                PermitStatus: properties.find(o => o.Key.endsWith('_PermitDetails'))?.Value || null,
                PermitDataFetched: properties.find(o => o.Key.endsWith('_PermitDetailsFetched'))?.Value || null,
            }

        }
        else {
            this._permitRequest = null;
        }
        this.permitRequest$.next(this._permitRequest);
    }

    /**
     * Removes empty rows from properties manipulating order -property
     * @param properties Array of TaskSpecificProperty
     */
    private removeEmptyRows(properties: Array<TaskSpecificProperty>): Array<TaskSpecificProperty> {
        const newProperties: Array<TaskSpecificProperty> = new Array<TaskSpecificProperty>;

        let prevOrderEdited = -1;
        let prevOrder = -1;
        properties.forEach(o => {
            // No need to change order for properties hidden from ui
            if (o.HideFromUI) {
                newProperties.push({ ...o });
            } else {
                const newObject: TaskSpecificProperty = { ...o };
                const prevOrderTmp: number = newObject.Order;

                // if the unchanged orders are the same for this and previous property we can use the order value of previous property
                if (prevOrder === newObject.Order) {
                    newObject.Order = prevOrderEdited;
                }

                // If order is zero, updated it to begin from order = 1
                else if (newObject.Order === 0) {
                    newObject.Order = 1;
                }

                // If order has empty row (i.e. difference between orders more than 1), give the row new order value
                else if (newObject.Order - prevOrderEdited > 1) {
                    if (prevOrderEdited === -1) {
                        newObject.Order = 1;
                    } else {
                        newObject.Order = prevOrderEdited + 1;
                    }
                }

                // Update the helper values
                prevOrderEdited = newObject.Order;
                prevOrder = prevOrderTmp;

                // Push the object to array
                newProperties.push(newObject);
            }
        });
        return newProperties;
    }
}
